import { Link } from "react-router-dom";
import { Mail, Pencil, Phone, Pin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusPill, initialsOf } from "@/components/patterns";
import { WhatsAppIcon } from "@/components/whatsapp/WhatsAppIcon";
import { usePins } from "@/lib/usePins";
import { useToast } from "@/context/ToastContext";
import { DoNotCallBadge } from "@/components/calling/DoNotCallBadge";
import { ROLE_LABEL, ROLE_TONE } from "@/lib/buyingRoles";
import type { Contact360 } from "@/types";

/** Who the person is, where they work, and how to reach them now */
export function ContactRecordHeader({
  data,
  name,
  can,
  onCall,
  onWhatsApp,
  onEmail,
  onEdit,
}: {
  data: Contact360;
  name: string;
  can: { update: boolean; whatsapp: boolean };
  onCall: () => void;
  onWhatsApp: () => void;
  onEmail: () => void;
  onEdit: () => void;
}) {
  const { contact, account } = data;
  const role = data.rollup?.buying_role;
  const phone = contact.mobile || contact.phone;
  const pins = usePins();
  const { error } = useToast();
  const { pathname } = window.location;
  const owner = contact.owner ? `${contact.owner.first_name} ${contact.owner.last_name || ""}`.trim() : "";

  return (
    <section aria-label="Contact summary" className="flex flex-wrap items-start gap-3.5 rounded-lg border border-border bg-card px-4 py-3.5">
      <span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-accent text-sm font-bold text-accent-foreground">{initialsOf(name)}</span>
      <div className="flex min-w-[240px] flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-[19px] font-semibold leading-tight tracking-tight">{name}</h1>
          {role && <StatusPill tone={ROLE_TONE[role] || "neutral"} dot={false}>{ROLE_LABEL[role] || role}</StatusPill>}
          {contact.is_primary && <StatusPill tone="primary" dot={false}>Primary contact</StatusPill>}
          <DoNotCallBadge type="CONTACT" id={contact.id} flagged={(contact as any).do_not_call} />
          <Button
            variant="outline"
            size="icon-sm"
            aria-pressed={pins.isPinned(pathname)}
            aria-label={pins.isPinned(pathname) ? "Unpin from Home" : "Pin to Home"}
            title={pins.isPinned(pathname) ? "Unpin from Home" : "Pin to Home"}
            onClick={() => pins.toggle(pathname, name).catch((e) => error("Could not change the pin", e?.message || "Try again"))}
          >
            <Pin className={`size-3.5 ${pins.isPinned(pathname) ? "fill-current text-gold" : ""}`} />
          </Button>
        </div>
        <div className="text-[13px] text-muted-foreground">
          {contact.job_title && <>{contact.job_title}{account ? " at " : ""}</>}
          {account && (
            <Link to={`/accounts/${account.id}`} className="font-medium text-primary hover:underline">
              {account.name}
            </Link>
          )}
          {account?.industry && <> · {account.industry}</>}
          {owner && (
            <>
              {" · Owner "}
              <strong className="font-medium text-foreground">{owner}</strong>
            </>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {phone && (
          <Button variant="outline" size="sm" onClick={onCall}>
            <Phone className="mr-1.5 size-3.5" />
            Call
          </Button>
        )}
        {contact.email && (
          <Button variant="outline" size="sm" onClick={onEmail}>
            <Mail className="mr-1.5 size-3.5" />
            Email
          </Button>
        )}
        {can.update && (
          <Button variant="outline" size="sm" onClick={onEdit}>
            <Pencil className="mr-1.5 size-3.5" />
            Edit
          </Button>
        )}
        {phone && can.whatsapp && (
          <Button size="sm" className="bg-sidebar text-white hover:bg-sidebar/90" onClick={onWhatsApp}>
            <WhatsAppIcon className="mr-1.5 size-3.5" />
            WhatsApp
          </Button>
        )}
      </div>
    </section>
  );
}
